import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { CountBadgePipe } from '../../../../../core/pipes/badge.pipe';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

@Component({
  selector: 'app-work-order-expiry-tracker',
  standalone: true,
  imports: [NgClass, CountBadgePipe, MatIconModule, MatProgressSpinnerModule, RouterLink],
  templateUrl: './work-order-expiry-tracker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkOrderExpiryTrackerComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() filters: any = {};
  @Input() requiredPermission?: string;
  @Input() title = 'Work Order Expiry Tracker';

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  workOrders: any[] = [];
  total = 0;
  loading = false;
  error: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.dashboardType) this.fetchData();
  }

  private fetchData(): void {
    this.loading = true;
    this.engine
      .getWidgetData(this.dashboardType, 'workOrderExpiry', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? res.record ?? {};
          this.workOrders = d.work_orders ?? [];
          this.total = d.total ?? this.workOrders.length;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getDaysLeft(endDate: string): number {
    const end = new Date(endDate).getTime();
    const now = new Date().getTime();
    return Math.max(0, Math.ceil((end - now) / 86400000));
  }
}
